import { Box, Stack } from '@chakra-ui/react';
import { Workshop } from 'api/types';
import Link from 'components/Link';
import { useUserContext } from 'context/UserContextProvider';
import { get, isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IdentificationResultIcon } from '../../components/Icons';
import { StatusTag } from '../../components/StatusTag';
import { DataTable } from '../../components/Table/DataTable';
import { SelectColumnFilter } from '../../components/Table/Filter';
import { MultiSelectDropDown } from '../../components/Table/MultiSelectDropDown';
import { createDropdownTranslatedStaticOptions } from '../../helpers/general';
import { useWorkshopVerificationRequestsQuery } from '../../hooks/useWorkshopVerificationRequestsQuery';
import { ContentCard } from '../../layout/ContentCard';

const WorkshopVerificationOverviewCard = (props: { autoFocus?: boolean }) => {
  const { autoFocus = true } = props;
  const { t } = useTranslation();

  const { statusCodes, config } = useUserContext();

  const [filtersSelected, setFiltersSelected] = useState(false);

  const workshopVerificationCodes = get(statusCodes, 'workshop_verification_request', {});

  const dropdownCountries = Object.values(config?.countries);

  const initialFilters = {
    searchString: '',
    itemsPerPage: 20,
    status: [100],
    assignee: undefined,
    country: undefined,
  };
  const localStorageData = window.localStorage.getItem('workshop_verification_requests_filters');

  // @ts-ignore
  const destringifiedData = JSON.parse(localStorageData) || {};
  useEffect(() => {
    if (!localStorageData) {
      window.localStorage.setItem('workshop_verification_requests_filters', JSON.stringify(initialFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchString, setSearchString] = useState(destringifiedData?.searchString || initialFilters?.searchString);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(destringifiedData?.itemsPerPage || initialFilters?.itemsPerPage);
  const [status, setStatus] = useState(destringifiedData?.status || initialFilters?.status);
  const [assignee, setAssignee] = useState(destringifiedData?.assignee || initialFilters?.assignee);
  const [country, setCountry] = useState(destringifiedData?.country || initialFilters?.country);

  const workshopVerificationRequestQuery = useWorkshopVerificationRequestsQuery({
    searchString,
    page,
    itemsPerPage,
    status: status?.join(','),
    country,
  });

  // @ts-ignore
  const verifications = workshopVerificationRequestQuery.data?.data || [];
  // @ts-ignore
  const currentPage = workshopVerificationRequestQuery.data?.meta?.current_page - 1;

  const initialLoad = searchString === '' && itemsPerPage === 20 && page === 1 && status === undefined && isNil(assignee) && isNil(country);

  useEffect(() => {
    setPage(1);
  }, [searchString, itemsPerPage, status, assignee, country]);

  const allStatusSelected = status.length === Object.keys(workshopVerificationCodes).length;
  useEffect(() => {
    setFiltersSelected(!isNil(country) || status?.length !== 0);

    window.localStorage.setItem(
      'workshop_verification_requests_filters',
      JSON.stringify({
        searchString: searchString,
        status: status,
        assignee: assignee,
        itemsPerPage: itemsPerPage,
        country: country,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString, status, itemsPerPage, country]);

  // todo figure out how to get relevant data about assignee.label

  const columns = useMemo(
    () => [
      {
        Header: t('forms:id.label'),
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return <Link to={`/app/op/workshop-verification-request/${row.original.id}`}>{row.original.user_readable_id}</Link>;
        },
      },
      {
        Header: t('forms:status.label'),
        accessor: 'status',
        shouldFilter: true,
        Filter: () =>
          MultiSelectDropDown(
            status,
            setStatus,
            'workshop_verification_request',
            workshopVerificationCodes,
            allStatusSelected,
            t('forms:status.label'),
          ),

        // @ts-ignore
        Cell: ({ value }) => <StatusTag resourceKey={'workshop_verification_request'} currentStatus={value} />,
      },
      {
        Header: t('forms:assignee.label'),
        accessor: 'open_task.assignee.name',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          const assignee = row?.original?.process_by?.name;
          return <Box> {assignee ?? '-'}</Box>;
        },
      },
      // {
      //   Header: t('forms:date.label'),
      //   accessor: (row: { completed_at: string }) => dateTimezoneTransformer(row?.completed_at, 'Europe/Vienna', false) ?? '-',
      // },
      {
        Header: t('workshop:name.label'),
        accessor: (row: { workshop: Workshop }) => row.workshop?.name,
      },
      {
        Header: t('workshop:email.label'),
        accessor: (row: { workshop: Workshop }) => row.workshop?.email,
      },
      {
        Header: t('workshop:country.label'),
        accessor: (row: { workshop: Workshop }) => {
          return t(`location:countries.${row?.workshop?.country}`);
        },
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: country,
            column,
            dropdownOptions: createDropdownTranslatedStaticOptions({
              records: dropdownCountries,
              translationKey: 'location:countries',
            }),
            onChange: setCountry,
          }),
      },
      {
        id: 'button',
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Link to={`/app/op/workshop-verification-request/${row.original.id}`} data-test-id={'edit-' + row.index}>
              {t('common:edit')}
            </Link>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workshopVerificationCodes, allStatusSelected, initialLoad, searchString, status, assignee, itemsPerPage, page, country],
  );

  return (
    <ContentCard
      icon={<IdentificationResultIcon boxSize={10} />}
      minH="fit-content"
      header={t('workshop_verification_request:header', 'Verifizierungen')}
      data-test-id="verifications-overview-card"
    >
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w="full">
          <DataTable
            resetFilter={() => {
              setSearchString(initialFilters?.searchString);
              setPage(1);
              setStatus(initialFilters?.status);
              setItemsPerPage(initialFilters?.itemsPerPage);
              setCountry(initialFilters?.country);
              setAssignee(initialFilters?.assignee);
              window.localStorage.setItem('workshop_verification_requests_filters', JSON.stringify(initialFilters));
            }}
            data-test-id="verifications-table"
            filtersSelected={filtersSelected}
            data={verifications}
            globalFilter={searchString}
            // @ts-ignore
            setGlobalFilter={setSearchString}
            autoFocus={autoFocus}
            globalFilterPlaceholder={t('workshop_verification_request:search')}
            // @ts-ignore
            hasFilters={true}
            hasSearchInput={true}
            currentPage={currentPage}
            perPage={itemsPerPage}
            // @ts-ignore
            total={workshopVerificationRequestQuery.data?.meta?.last_page || 1}
            columns={columns}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            loading={workshopVerificationRequestQuery.isFetching || workshopVerificationRequestQuery.isLoading}
            // @ts-ignore
            isMoreThenOnePage={!initialLoad || Boolean(workshopVerificationRequestQuery.data?.meta?.next_page_url) || currentPage > 0}
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default WorkshopVerificationOverviewCard;
